/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';

import {TypedUseQuery} from '@reduxjs/toolkit/query/react';

import {axiosBaseQuery} from 'src/models';

import {ListError, ListNoData, ListNotFound, ListPending} from './List';
import {SKIP_SYMBOL, TPayload, TUseQuery} from './useList';

type TItem = any;
type TFilters = any;

const Page = React.memo<{
  useQuery: TUseQuery<TItem, TFilters>;
  payload: TPayload;
  itemComponent?: React.ComponentType<any>;
  renderItem?: (item: TItem, index: number, readOnly?: boolean) => React.ReactNode;
  renderItems?: (items: TItem[]) => React.ReactNode;
  hasFilters?: boolean;
  errorNode?: React.ReactNode;
  noDataNode?: React.ReactNode;
  notFoundNode?: React.ReactNode;
  pendingNode?: React.ReactNode;
  isShowRefetch?: boolean;
  readOnly?: boolean;
}>(
  ({
    useQuery,
    payload,
    itemComponent: Item,
    renderItem,
    renderItems,
    hasFilters,
    isShowRefetch,
    errorNode = <ListError />,
    noDataNode = <ListNoData />,
    notFoundNode = <ListNotFound />,
    pendingNode = <ListPending />,
    readOnly = false,
  }) => {
    const {data, error, isLoading, isFetching} = useQuery(payload, {
      // @ts-expect-error: ok
      skip: payload[SKIP_SYMBOL],
    });

    if (!data?.content.length && !data?.first) {
      return null;
    }

    if (isLoading || (isShowRefetch && isFetching)) {
      return <>{pendingNode}</>;
    }

    if (error) {
      return <>{errorNode}</>;
    }

    if (!data?.content.length) {
      return hasFilters ? <>{notFoundNode}</> : <>{noDataNode}</>;
    }

    if (renderItems) {
      return <>{renderItems(data.content)}</>;
    }

    return (
      <>
        {data.content.map((item, index) => {
          if (renderItem) {
            return renderItem(item, index, readOnly);
          }

          if (Item) {
            return <Item item={item} key={item.id} readOnly={readOnly} />;
          }
        })}
      </>
    );
  },
);

const SinglePage = React.memo<{
  useQuery: TypedUseQuery<TItem[], any, ReturnType<typeof axiosBaseQuery>>;
  payload?: any;
  itemComponent?: React.ComponentType<any>;
  renderItem?: (item: TItem, index: number, readOnly?: boolean) => React.ReactNode;
  renderItems?: (items: TItem[]) => React.ReactNode;
  hasFilters?: boolean;
  errorNode?: React.ReactNode;
  noDataNode?: React.ReactNode;
  notFoundNode?: React.ReactNode;
  pendingNode?: React.ReactNode;
  isShowRefetch?: boolean;
  readOnly?: boolean;
  skip?: boolean;
}>(
  ({
    useQuery,
    payload,
    itemComponent: Item,
    renderItem,
    renderItems,
    hasFilters,
    isShowRefetch,
    errorNode = <ListError />,
    noDataNode = <ListNoData />,
    notFoundNode = <ListNotFound />,
    pendingNode = <ListPending />,
    readOnly = false,
    skip = false,
  }) => {
    const {data, error, isLoading, isFetching} = useQuery(payload, {skip});

    if (isLoading || (isShowRefetch && isFetching)) {
      return <>{pendingNode}</>;
    }

    if (error) {
      return <>{errorNode}</>;
    }

    if (!data?.length) {
      return hasFilters ? <>{notFoundNode}</> : <>{noDataNode}</>;
    }

    if (renderItems) {
      return <>{renderItems(data)}</>;
    }

    return (
      <>
        {data.map((item, index) => {
          if (renderItem) {
            return renderItem(item, index, readOnly);
          }

          if (Item) {
            return <Item item={item} key={item.id} readOnly={readOnly} />;
          }
        })}
      </>
    );
  },
);

export {Page, SinglePage};
/* eslint-enable @typescript-eslint/no-explicit-any */
